import {Profile, User} from '../../types/api';
import {
    apiDelete,
    changeHolder,
    changeOwner,
    clearEntityCache,
    createListResponse,
    get,
    getCache,
    list,
    ListResponse,
    post,
    PostFn,
    put,
    PutFn,
    RequestConfig,
    RequestOptions,
} from './CRUD';
import {AxiosRequestConfig} from 'axios';
import Api from '../../lib/apiClient';
import {logError} from '../../sentry';
import {clearCachesByTag} from '../../lib/cache';
import {
    deleteEntityFile,
    ImageType,
    TypedUploadActionProps,
    uploadEntityFile,
} from './UploadApi';
import {RecursivePartial} from '../../types/list';
import {BatchEditInput} from './KompanyApi';
import {RowData, SheetFilters} from '../../components/Sheet/types';
import {getThrottle} from '../../lib/requestListThrottle';
import {profileSignatureCacheTag} from './SignatureModelApi';
import qs from 'qs';
import {config} from '../../config';
import {EntityName} from './entities.ts';

export const listProfiles = (
    options?: RequestOptions,
    config: RequestConfig = {},
) => list<Profile>(EntityName.Profile, options, config);

/**
 * Label used to tell profiles apart in selects: appends the internal
 * reference, which is often the only thing distinguishing two profiles
 * of the same person.
 */
export function getProfileSelectLabel(
    profile: Pick<Profile, 'profileLabel' | 'internalName'>,
): string {
    return profile.internalName
        ? `${profile.profileLabel} (${profile.internalName})`
        : profile.profileLabel;
}

export const listOrganizationProfiles = (
    organizationId: string,
    options?: RequestOptions,
    config: RequestConfig = {},
) =>
    listProfiles(
        {
            ...options,
            params: {
                ...(options?.params ?? {}),
                organizationId,
            },
        },
        config,
    );
export const listHolderProfiles = (
    holderId: string,
    options?: RequestOptions,
    config: RequestConfig = {},
) =>
    listProfiles(
        {
            ...options,
            params: {
                ...(options?.params ?? {}),
                holder: holderId,
            },
            cache: getCache(`${EntityName.Profile}:holder:${holderId}`, [
                EntityName.Profile,
            ]),
        },
        config,
    );

export const listProfileHolders = (
    options?: RequestOptions,
    config: RequestConfig = {},
) => list<Profile>(`${EntityName.Profile}/holders`, options, config);

export const getProfile = (
    id: string,
    safeKey?: string,
    config?: AxiosRequestConfig,
) =>
    get<Profile>(EntityName.Profile, id, {
        ...config,
        params: {
            ...(config?.params ?? {}),
            safeKey,
        },
    });

export const getProfileThrottle = async (id: string): Promise<Profile> => {
    return await getThrottle(EntityName.Profile, id, async ids => {
        return (
            await listProfiles({
                params: {
                    ids,
                },
            })
        ).items;
    });
};

export const putProfile: PutFn<
    Profile,
    RecursivePartial<ProfileWithMediumInput>
> = async (id, data) => {
    delete data.safeKey;
    delete data.vcfUrl;
    const res = await put<Profile, RecursivePartial<ProfileWithMediumInput>>(
        EntityName.Profile,
        id,
        data,
    );
    clearDependenciesOfPerson();

    return res;
};

export type ProfileWithMediumInput = {
    withMedium?: boolean;
    medium?:
        | {
              printContext?: number;
              productCode?: string;
              order?: string | null;
              assigned?: boolean;
              activated?: boolean;
              owner?: User;
              holder?: User;
          }
        | undefined;
    holderInvite?: {
        email: string;
        fullName: string;
    };
    assignOwner?: boolean;
    dryRun?: boolean;
    step?: number;
} & Profile;

export const postProfile: PostFn<
    Profile,
    RecursivePartial<ProfileWithMediumInput>
> = async data => {
    const r = (await post(EntityName.Profile, data)) as Profile;

    clearCachesByTag(EntityName.User);
    clearCachesByTag(EntityName.MemberSuggestion);

    return r;
};

export const duplicateProfile = async (
    id: string,
    data: {internalName?: string; firstName?: string; lastName?: string},
) => {
    const r = (await Api.post(`/${EntityName.Profile}/${id}/duplicate`, data))
        .data as Profile;

    clearCachesByTag(EntityName.User);
    clearCachesByTag(EntityName.MemberSuggestion);
    clearDependenciesOfPerson();

    return r;
};

export const changeProfileOwner = async (id: string, userId: string) => {
    const res = await changeOwner<Profile>(EntityName.Profile, id, userId);
    clearEntityCache(EntityName.Profile, id);
    clearDependenciesOfPerson();

    return res;
};

export const changeProfileHolder = async (
    id: string,
    userId: string | null,
) => {
    const res = await changeHolder<Profile>(EntityName.Profile, id, userId);
    clearEntityCache(EntityName.Profile, id);
    clearCachesByTag(EntityName.Medium);

    return res;
};

export const deleteProfile = async (id: string) => {
    await apiDelete(EntityName.Profile, id);
    clearDependenciesOfPerson();
};

export async function logProfileView(id: string): Promise<void> {
    try {
        await Api.post(`/profile-views/${id}/log`, undefined, {
            'anonymous': true,
            'axios-retry': {
                retries: 3,
            },
        } as AxiosRequestConfig<undefined>);
    } catch (e) {
        logError(e, {
            tags: {
                section: 'profile-log',
            },
        });
    }
}

export async function uploadProfileImage(
    props: TypedUploadActionProps,
): Promise<Profile> {
    const res = await uploadEntityFile<Profile>({
        entity: EntityName.Profile,
        ...props,
    });

    clearDependenciesOfPerson();

    return res;
}

export const deleteProfileImage = async (
    id: string,
    type: ImageType,
): Promise<Profile> => {
    const res = await deleteEntityFile<Profile>(EntityName.Profile, type, id);
    clearDependenciesOfPerson();

    return res;
};

export const getKompanyProfiles = async (id: string) => {
    return await listProfiles(
        {
            params: {
                kompany: id,
            },
        },
        {
            noCache: true,
        },
    );
};

export type LeadWelcomeEmailSampleInput = {
    subject?: string | null;
    body?: string | null;
};

export async function sendLeadWelcomeEmailSample(
    id: string,
    data: LeadWelcomeEmailSampleInput,
): Promise<void> {
    await Api.post(
        `/${EntityName.Profile}/${id}/lead-welcome-email-sample`,
        data,
    );
}

export type TransferProfileInput = {
    target: string;
    applyToProfiles: boolean;
    applyToMediums: boolean;
    applyToLeads: boolean;
};

export const transferProfile = async (
    id: string,
    data: TransferProfileInput,
) => {
    const result = await post<Profile, TransferProfileInput>(
        `${EntityName.Profile}/${id}/transfer`,
        data,
    );

    clearEntityCache(EntityName.Profile, id);
    clearDependenciesOfPerson();

    return result;
};

export async function putProfileBatchEdit(data: BatchEditInput): Promise<
    WithProfileCreditsLeft<{
        data: RowData[];
    }>
> {
    const r = await Api.post(`${EntityName.Profile}/batch-edit`, data);
    clearEntityCache(EntityName.Profile);
    clearDependenciesOfPerson();

    return r.data;
}

export type WithProfileCreditsLeft<T> = T & {
    profileCreditsLeft: number | undefined;
};

export async function getProfileBatchEdit(
    organizationId: string,
    filters: SheetFilters,
): Promise<WithProfileCreditsLeft<ListResponse<RowData>>> {
    const r = (
        await Api.get(`${EntityName.Profile}/batch-edit`, {
            params: {
                ...filters,
                organizationId,
            },
        })
    ).data;

    return {
        ...createListResponse<RowData>(r),
        profileCreditsLeft: r.profileCreditsLeft,
    };
}

export type ProfileLeadStatusResponse = {
    totalProfiles: number;
    totalEnabled: number;
};

export type VcfOptionsInput = {
    avatar?: boolean;
    links?: boolean;
    addresses?: boolean;
    note?: boolean;
};

export type QrCodeOptionsInput = {
    offline?: boolean;
    vcf?: VcfOptionsInput;
};

export type WalletPassOptionsInput = {
    offline?: boolean;
    vcf?: VcfOptionsInput;
};

export function getProfileQrCodeUrl(
    profileId: string,
    safeKey: string | undefined,
    data: QrCodeOptionsInput,
): string {
    const baseUrl = `${config.apiUrl}/${EntityName.Profile}/${profileId}/qrcode`;
    const queryString = qs.stringify({
        safeKey,
        ...data,
    });

    return `${baseUrl}?${queryString}`;
}

export type AddToWalletFn = (
    profileId: string,
    safeKey: string | undefined,
    data: WalletPassOptionsInput,
) => Promise<void>;

export const addToAppleWallet: AddToWalletFn = async (
    profileId,
    safeKey,
    data,
) => {
    // For React Native app to intercept the request from a WebView, it has to be a classic GET navigation request:
    const baseUrl = `${config.apiUrl}/${EntityName.Profile}/${profileId}/wallet-pass/apple`;
    const queryString = qs.stringify({
        safeKey,
        ...data,
    });
    window.location.href = `${baseUrl}?${queryString}`;
};

export const addToGoogleWallet: AddToWalletFn = async (
    profileId,
    safeKey,
    data,
) => {
    const {url} = (
        await Api.post<{url: string}>(
            `${EntityName.Profile}/${profileId}/wallet-pass/google`,
            data,
            {params: {safeKey}},
        )
    ).data;
    window.open(url, '_blank');
};

export function clearDependenciesOfPerson() {
    clearCachesByTag(EntityName.Asset);
    clearCachesByTag(EntityName.Medium);
    clearCachesByTag(profileSignatureCacheTag);
}
